import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { BRAND } from '../config/brand';

const CHECK_EVERY_MS = 5 * 60 * 1000;

/** The hashed app bundle a page loads, e.g. "/assets/index-AbC123.js". */
const bundleOf = (html) => html.match(/<script[^>]+type="module"[^>]+src="([^"]*\/assets\/index-[^"]+\.js)"/)?.[1] || null;

/**
 * "A new version of Carduzo is available — Reload".
 * An open tab keeps running the version it loaded (single-page app), so after a deploy it can
 * show old screens and send old data (e.g. old prices to "Set up Stripe"). Every few minutes,
 * and when the tab comes back into view, compare our bundle with the one the server now serves.
 * Not shown on public cards (/c/...), where visitors just look at a card.
 */
export default function UpdateBanner() {
  const { pathname } = useLocation();
  const [stale, setStale] = useState(false);

  useEffect(() => {
    if (import.meta.env.DEV) return undefined;
    const mine = document.querySelector('script[type="module"][src*="/assets/index-"]')?.getAttribute('src');
    if (!mine) return undefined;
    let busy = false;
    const check = async () => {
      if (busy || document.visibilityState !== 'visible') return;
      busy = true;
      try {
        const res = await fetch(`/?v=${Date.now()}`, { cache: 'no-store' });
        const live = res.ok ? bundleOf(await res.text()) : null;
        if (live && live !== mine) setStale(true);
      } catch { /* offline: try again later */ }
      busy = false;
    };
    const timer = setInterval(check, CHECK_EVERY_MS);
    document.addEventListener('visibilitychange', check);
    window.addEventListener('focus', check);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', check);
      window.removeEventListener('focus', check);
    };
  }, []);

  if (!stale || pathname.startsWith('/c/')) return null;
  return (
    <div role="status" className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 w-[calc(100%-32px)] max-w-md"
      data-testid="update-banner">
      <div className="flex items-center gap-3 rounded-xl bg-navy text-white ring-1 ring-white/25 px-4 py-3 shadow-[0_12px_32px_rgba(11,21,48,0.35)]">
        <span className="flex-1 text-[15px]">A new version of {BRAND.name} is available.</span>
        <button type="button" onClick={() => window.location.reload()}
          className="h-9 px-4 rounded-lg bg-coral text-navy text-sm font-semibold hover:bg-[#ff7774] shrink-0">
          Reload
        </button>
        <button type="button" onClick={() => setStale(false)} aria-label="Dismiss"
          className="h-9 w-9 rounded-lg text-[#C9D6E6] hover:bg-white/10 shrink-0 text-lg leading-none">×</button>
      </div>
    </div>
  );
}
